import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Users } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resolveImageUrl } from '../lib/imageUpload';

const Home = () => {
  const { settings } = useSiteSettings();
  const HERO_SCALE = {
    small: 'max-h-24 max-w-[200px] md:max-w-[260px]',
    medium: 'max-h-40 max-w-[340px] md:max-w-[440px]',
    large: 'max-h-56 max-w-[520px] md:max-w-[720px]',
  };
  const heroScale = HERO_SCALE[settings.hero_image_scale] || HERO_SCALE.medium;
  const HERO_SUBTITLE_SCALE = {
    small: 'text-base md:text-lg',
    medium: 'text-xl md:text-2xl',
    large: 'text-2xl md:text-3xl',
  };
  const heroSubtitleScale = HERO_SUBTITLE_SCALE[settings.hero_subtitle_scale] || HERO_SUBTITLE_SCALE.medium;
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center border-b-2 border-black mt-20" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${resolveImageUrl(settings.hero_bg_url)}')`,
          }}
        />
        <div className="absolute inset-0 bg-white/60" />
        <div className="relative z-10 text-center px-4">
          {settings.hero_image_url ? (
            <img
              src={resolveImageUrl(settings.hero_image_url)}
              alt={settings.site_name}
              className={`mx-auto mb-2 md:mb-3 ${heroScale} h-auto w-auto object-contain`}
              data-testid="hero-logo"
            />
          ) : (
            <h1 className="text-6xl md:text-8xl font-bold tracking-tighter leading-[0.9] mb-2 md:mb-3" style={{ fontFamily: 'var(--heading-font)' }} data-testid="hero-title">
              {settings.hero_title}
            </h1>
          )}
          <p className={`${heroSubtitleScale} text-zinc-700 mb-12 max-w-2xl mx-auto`} data-testid="hero-subtitle">
            {settings.hero_subtitle}
          </p>
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/classes">
                <button className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-8 py-6 text-lg font-bold transition-transform hover:scale-105 active:scale-95" data-testid="hero-book-class-btn">
                  BOOK A CLASS
                </button>
              </Link>
              <Link to="/studios">
                <button className="bg-black text-white hover:bg-zinc-800 rounded-full px-8 py-6 text-lg font-medium" data-testid="hero-book-space-btn">
                  BOOK YOUR SPACE
                </button>
              </Link>
            </div>
            <Link to="/class-passes">
              <button className="bg-white text-black border-2 border-black hover:bg-black hover:text-white rounded-full px-8 py-6 text-lg font-bold transition-colors" data-testid="hero-purchase-pass-btn">
                PURCHASE A CLASS PASS
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="marquee-container" data-testid="marquee">
        <div className="marquee-content">
          <span className="text-2xl md:text-3xl font-bold mx-8" style={{ fontFamily: 'var(--heading-font)' }}>
            BOOK YOUR SPACE • JOIN A CLASS • ELEVATE YOUR CRAFT • BOOK YOUR SPACE • JOIN A CLASS • ELEVATE YOUR CRAFT •
          </span>
          <span className="text-2xl md:text-3xl font-bold mx-8" style={{ fontFamily: 'var(--heading-font)' }}>
            BOOK YOUR SPACE • JOIN A CLASS • ELEVATE YOUR CRAFT • BOOK YOUR SPACE • JOIN A CLASS • ELEVATE YOUR CRAFT •
          </span>
        </div>
      </div>

      {/* Features */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-4 md:px-8" data-testid="features-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white border border-zinc-100 p-8 hover:border-black transition-colors duration-300" data-testid="feature-classes">
            <Calendar size={48} className="mb-6" />
            <h3 className="text-2xl md:text-3xl font-semibold mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
              Weekly Classes
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-zinc-600 mb-6" data-testid="home-classes-desc">
              {settings.home_classes_desc}
            </p>
            <Link to="/classes" className="inline-flex items-center gap-2 font-bold hover:gap-4 transition-all" data-testid="view-schedule-link">
              VIEW SCHEDULE <ArrowRight size={20} />
            </Link>
          </div>

          <div className="bg-white border border-zinc-100 p-8 hover:border-black transition-colors duration-300" data-testid="feature-studios">
            <MapPin size={48} className="mb-6" />
            <h3 className="text-2xl md:text-3xl font-semibold mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
              The Studio
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-zinc-600 mb-6" data-testid="home-studio-desc">
              {settings.home_studio_desc}
            </p>
            <Link to="/studios" className="inline-flex items-center gap-2 font-bold hover:gap-4 transition-all" data-testid="explore-studios-link">
              EXPLORE THE STUDIO <ArrowRight size={20} />
            </Link>
          </div>

          <div className="bg-white border border-zinc-100 p-8 hover:border-black transition-colors duration-300" data-testid="feature-community">
            <Users size={48} className="mb-6" />
            <h3 className="text-2xl md:text-3xl font-semibold mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
              Community
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-zinc-600 mb-6" data-testid="home-community-desc">
              {settings.home_community_desc}
            </p>
            <a
              href="https://instagram.com/studioatthethird"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-bold hover:gap-4 transition-all"
              data-testid="join-us-link"
            >
              JOIN US <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
            {settings.site_name}
          </h2>
          <p className="text-zinc-400">Philippines</p>
          <p className="text-zinc-400 mt-4 text-sm">© 2025 Studio at the Third. All rights reserved.</p>
          <Link
            to="/admin/login"
            className="inline-block mt-4 text-xs font-medium text-zinc-500 hover:text-[var(--accent)] transition-colors"
            data-testid="footer-admin-link"
          >
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Home;